import { useState } from 'react';
import { useT } from '@kmgestao/i18n';
import { Badge, Button, PageSectionCard, Skeleton } from '@kmgestao/ui';

import {
  formatBRL,
  formatDateBR,
  useOwnInvoices,
  type OwnInvoiceRow,
} from '../../../features/clinic-billing/index.js';

const PAGE_SIZE = 12;

export interface InvoiceHistoryCardProps {
  clinicId: string;
}

/**
 * Card "Histórico financeiro" — todas as faturas da clinic, mais
 * recentes primeiro. Inclui SaaS recurring + parcelas de consultoria
 * (distinguidas via badge).
 *
 * **Paginação cumulativa** ("Carregar mais"): primeira página com 12,
 * cliques agregam mais 12. Estado local `pages` controla quantas
 * páginas já buscamos. UX mobile-friendly — owner não precisa pular
 * pra outra página, scroll natural.
 */
export function InvoiceHistoryCard({ clinicId }: InvoiceHistoryCardProps) {
  const { t } = useT('clinics');
  const [pageCount, setPageCount] = useState(1);

  // Acumula todas as páginas em um único array. Cada página é uma
  // useQuery separada, cacheada independentemente — owner pode voltar
  // depois sem refetch das anteriores.
  return (
    <PageSectionCard
      title={t('plan.history.title')}
      description={t('plan.history.description')}
      contentClassName="space-y-4"
    >
      <InvoicePages
        clinicId={clinicId}
        pageCount={pageCount}
        onLoadMore={() => setPageCount((p) => p + 1)}
      />
    </PageSectionCard>
  );
}

function InvoicePages({
  clinicId,
  pageCount,
  onLoadMore,
}: {
  clinicId: string;
  pageCount: number;
  onLoadMore: () => void;
}) {
  const { t } = useT('clinics');
  // Renderiza N InvoicePage (uma por página acumulada). Cada uma é um
  // useQuery independente — cache funciona naturalmente. Não usamos
  // `useInfiniteQuery` pra ficar simples e evitar a complexidade do
  // pageParam (não precisamos hoje).
  const pages = Array.from({ length: pageCount }, (_, i) => i);
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-2 font-medium">{t('plan.history.columns.dueDate')}</th>
              <th className="p-2 font-medium">{t('plan.history.columns.kind')}</th>
              <th className="p-2 font-medium">{t('plan.history.columns.amount')}</th>
              <th className="p-2 font-medium">{t('plan.history.columns.status')}</th>
              <th className="p-2 font-medium">{t('plan.history.columns.paidAt')}</th>
              <th className="p-2 font-medium">
                <span className="sr-only">{t('plan.history.columns.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pages.map((page) => (
              <PageRows key={page} clinicId={clinicId} page={page} />
            ))}
          </tbody>
        </table>
      </div>
      <LoadMoreFooter clinicId={clinicId} pageCount={pageCount} onLoadMore={onLoadMore} />
    </div>
  );
}

function PageRows({ clinicId, page }: { clinicId: string; page: number }) {
  const { t } = useT('clinics');
  const q = useOwnInvoices(clinicId, { page, pageSize: PAGE_SIZE });

  if (q.isLoading) {
    return (
      <>
        {[0, 1, 2].map((i) => (
          <tr key={i} className="border-b border-border">
            <td colSpan={6} className="px-2 py-3">
              <Skeleton className="h-5 w-full" />
            </td>
          </tr>
        ))}
      </>
    );
  }

  if (q.isError) {
    return (
      <tr className="border-b border-border">
        <td colSpan={6} className="px-2 py-3 text-destructive">
          <span role="alert">
            {t('plan.history.loadError', {
              message: q.error instanceof Error ? q.error.message : '',
            })}
          </span>
        </td>
      </tr>
    );
  }

  const rows = q.data?.rows ?? [];
  if (page === 0 && rows.length === 0) {
    return (
      <tr className="border-b border-border">
        <td colSpan={6} className="px-2 py-6 text-center text-muted-foreground">
          {t('plan.history.empty')}
        </td>
      </tr>
    );
  }

  return (
    <>
      {rows.map((invoice) => (
        <InvoiceRow key={invoice.id} invoice={invoice} />
      ))}
    </>
  );
}

function InvoiceRow({ invoice }: { invoice: OwnInvoiceRow }) {
  const { t } = useT('clinics');
  const isConsulting = invoice.subscription_id == null;
  const dueDate = formatDateBR(invoice.due_date);
  const paidAt = formatDateBR(invoice.paid_at);
  return (
    <tr className="border-b border-border">
      <td className="px-2 py-3 text-foreground">{dueDate ?? '—'}</td>
      <td className="px-2 py-3">
        <Badge variant="outline">
          {isConsulting ? t('plan.history.kindConsulting') : t('plan.history.kindSaas')}
        </Badge>
      </td>
      <td className="px-2 py-3 font-medium text-foreground">{formatBRL(invoice.amount_brl)}</td>
      <td className="px-2 py-3">
        <Badge variant={statusBadgeVariant(invoice.status)}>
          {t(`plan.history.statusLabels.${invoice.status}`, {
            defaultValue: invoice.status,
          })}
        </Badge>
      </td>
      <td className="px-2 py-3 text-muted-foreground">{paidAt ?? '—'}</td>
      <td className="px-2 py-3 text-right">
        {invoice.invoice_url ? (
          <a
            href={invoice.invoice_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {/* DB usa 'confirmed' (Asaas detectou pagamento) ou
                'received' (compensado). Antes checava 'paid' (genérico)
                que NÃO existe no enum — link "ver recibo" nunca aparecia. */}
            {invoice.status === 'confirmed' || invoice.status === 'received'
              ? t('plan.history.actions.viewReceipt')
              : t('plan.history.actions.openInvoice')}
          </a>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

function LoadMoreFooter({
  clinicId,
  pageCount,
  onLoadMore,
}: {
  clinicId: string;
  pageCount: number;
  onLoadMore: () => void;
}) {
  const { t } = useT('clinics');
  // Lê apenas a última página pra saber se tem mais.
  const lastPageQ = useOwnInvoices(clinicId, { page: pageCount - 1, pageSize: PAGE_SIZE });
  if (lastPageQ.isLoading || !lastPageQ.data) return null;
  const total = lastPageQ.data.total;
  const loaded = Math.min(pageCount * PAGE_SIZE, total);
  const hasMore = lastPageQ.data.hasMore;
  return (
    <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs text-muted-foreground">
        {t('plan.history.summary', { loaded, total })}
      </p>
      {hasMore ? (
        <Button type="button" variant="outline" onClick={onLoadMore}>
          {t('plan.history.actions.loadMore')}
        </Button>
      ) : null}
    </div>
  );
}

function statusBadgeVariant(
  status: string,
): 'default' | 'destructive' | 'success' | 'secondary' | 'outline' {
  switch (status) {
    // DB usa 'confirmed' (Asaas detectou pagamento, ainda não liquidado)
    // e 'received' (liquidado/recibo emitido). Ambos = badge verde.
    case 'confirmed':
    case 'received':
      return 'success';
    case 'pending':
      return 'default';
    case 'overdue':
      return 'destructive';
    case 'canceled':
    case 'refunded':
      // Badge não tem variant 'warning' — usamos 'secondary' (cor neutra-muted)
      return 'secondary';
    default:
      return 'outline';
  }
}
